// The CONNECTORS list (N-FDE-1 AC-5) — each Connector's state/health projection. It renders `integrations`'
// (H) state PROJECTION: `configured / connected / error`, `last_refresh`, SANITISED `last_error`, the verified
// org identity, and the active/revoked write scopes — and NEVER a credential value (G-140). The projection
// type structurally has no credential field, so there is nothing to leak; the surface only shows a boolean
// credential-PRESENCE marker, never a token.
//
// A missing system can be registered through the existing H route with only `{system}`. A configured/error
// Connector begins the live OAuth flow with only its Connector id; the credential remains SERVER-SIDE. A
// CONNECTED Connector gets the RE-CONSENT affordance instead (IFC-INTG-018): a wider permission set is granted
// only by a fresh consent, so a live connection needs its own way back to the provider — two-step confirmed,
// because reconnecting signs the platform out of that system until the consent finishes. Tokens only (G-181).

import { useState } from 'react';
import { Panel, StatusPill, EmptyState } from '../primitives.tsx';
import { MetaLine, connectorStateTone } from './operate-primitives.tsx';
import type { BeginConnectRoute, ConnectorSystemWire, RouteResult } from '../api-client.ts';
import type { ConnectorStateView } from '../operate-projections.ts';

/** A human system label (display only — the id/system enum stays the machine key). */
const SYSTEM_LABEL: Readonly<Record<string, string>> = {
  xero: 'Xero',
  hubspot: 'HubSpot',
};

export interface ConnectorListProps {
  readonly connectors: readonly ConnectorStateView[];
  /** Register one missing system at configured; the routed body contains only the system name. */
  readonly onRegister: (system: ConnectorSystemWire) => Promise<RouteResult>;
  /** Route an Operator connect-BEGIN intent for a `configured`/`error` Connector; the surface navigates. */
  readonly onBeginConnect: (connectorId: string) => Promise<BeginConnectRoute>;
  /** Route an Operator RE-CONSENT intent for a `connected` Connector; the surface navigates on ok. */
  readonly onReconnect: (connectorId: string) => Promise<BeginConnectRoute>;
}

/** The Connectors list — actionable missing-system registration plus a state/health card per Connector. */
export function ConnectorList({
  connectors,
  onRegister,
  onBeginConnect,
  onReconnect,
}: ConnectorListProps): React.JSX.Element {
  const missingSystems = (Object.keys(SYSTEM_LABEL) as ConnectorSystemWire[]).filter(
    (system) => !connectors.some((connector) => connector.system === system),
  );
  return (
    <div className="space-y-3">
      {missingSystems.length > 0 ? (
        <Panel>
          {connectors.length === 0 ? (
            <EmptyState>No connectors are configured yet.</EmptyState>
          ) : null}
          <div className="flex flex-wrap gap-2" aria-label="Add a connector">
            {missingSystems.map((system) => (
              <button
                key={system}
                type="button"
                data-register={system}
                onClick={() => void onRegister(system)}
                aria-label={`Add the ${SYSTEM_LABEL[system]} connector`}
                className="rounded-sm border border-bracken bg-bracken-tint px-3 py-1.5 text-sm font-medium text-bracken hover:border-border-strong"
              >
                Add {SYSTEM_LABEL[system]}
              </button>
            ))}
          </div>
        </Panel>
      ) : null}
      {connectors.length > 0 ? (
        <ul className="space-y-3" aria-label="Connectors">
          {connectors.map((connector) => (
            <li key={connector.connectorId}>
              <ConnectorCard
                connector={connector}
                onBeginConnect={onBeginConnect}
                onReconnect={onReconnect}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** The one button style every Connector action shares (the surface's existing affordance idiom). */
const ACTION_BUTTON =
  'shrink-0 rounded-sm border border-bracken bg-bracken-tint px-3 py-1.5 text-sm font-medium text-bracken hover:border-border-strong disabled:opacity-60';

/** One Connector card — state/health metadata + the routed OAuth action for its lifecycle state. */
function ConnectorCard({
  connector,
  onBeginConnect,
  onReconnect,
}: {
  readonly connector: ConnectorStateView;
  readonly onBeginConnect: (connectorId: string) => Promise<BeginConnectRoute>;
  readonly onReconnect: (connectorId: string) => Promise<BeginConnectRoute>;
}): React.JSX.Element {
  const systemLabel = SYSTEM_LABEL[connector.system] ?? connector.system;
  return (
    <Panel className="px-5 py-4" dataMarker={{ name: 'connector', value: connector.connectorId }}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="ritona-voice text-lg text-text">{systemLabel}</span>
            <StatusPill tone={connectorStateTone(connector.connectionState)}>
              {connector.connectionState}
            </StatusPill>
          </div>
          <ConnectorMeta connector={connector} />
        </div>
        {connector.connectionState === 'configured' || connector.connectionState === 'error' ? (
          // The live OAuth connect begins here (Story H-1): the routed intent carries ONLY the Connector id;
          // the returned authorize URL (anti-forgery state + public client_id, never a secret — G-058) is
          // where the surface navigates the Operator's browser.
          <button
            type="button"
            data-connect={connector.connectorId}
            onClick={() => void onBeginConnect(connector.connectorId)}
            aria-label={`${connector.connectionState === 'error' ? 'Reconnect' : 'Connect'} the ${systemLabel} connector`}
            className={ACTION_BUTTON}
          >
            {connector.connectionState === 'error' ? 'Reconnect' : 'Connect'}
          </button>
        ) : null}
        {connector.connectionState === 'connected' ? (
          <ReconnectControl
            connectorId={connector.connectorId}
            systemLabel={systemLabel}
            onReconnect={onReconnect}
          />
        ) : null}
      </div>
      <ConnectorScopes scopes={connector.scopes} />
    </Panel>
  );
}

/**
 * The RE-CONSENT affordance for a HEALTHY Connector (IFC-INTG-018). Wider permissions are granted only by a
 * fresh consent — a refresh never widens what a connection may read — so a live Connector needs its own way
 * back to the provider's permission screen. It is TWO-STEP CONFIRMED (the Task-delete precedent): reconnecting
 * hands the old permission back, so nothing reads from that system until the operator finishes at the
 * provider. On ok the surface navigates to the provider; a refusal renders its sanitised note in place and
 * goes NOWHERE (fail-closed) — the routed intent carries only the Connector id, never a credential (G-140).
 */
function ReconnectControl({
  connectorId,
  systemLabel,
  onReconnect,
}: {
  readonly connectorId: string;
  readonly systemLabel: string;
  readonly onReconnect: (connectorId: string) => Promise<BeginConnectRoute>;
}): React.JSX.Element {
  const control = useReconnect(connectorId, onReconnect);
  return (
    <div className="w-56 shrink-0 text-right">
      {control.confirming ? (
        <ReconnectConfirmation
          connectorId={connectorId}
          systemLabel={systemLabel}
          busy={control.busy}
          onCancel={() => control.setConfirming(false)}
          onConfirm={control.reconnect}
        />
      ) : (
        <button
          type="button"
          onClick={() => control.setConfirming(true)}
          aria-label={`Reconnect the ${systemLabel} connector`}
          className={ACTION_BUTTON}
        >
          Reconnect
        </button>
      )}
      {control.note !== null ? (
        <p role="status" className="mt-2 text-xs text-text-subtle">
          {control.note}
        </p>
      ) : null}
    </div>
  );
}

/** Reconnect-control state: arm → explicit confirm routes the intent; a refusal surfaces its note in place. */
function useReconnect(
  connectorId: string,
  onReconnect: (connectorId: string) => Promise<BeginConnectRoute>,
): {
  readonly confirming: boolean;
  readonly setConfirming: (next: boolean) => void;
  readonly busy: boolean;
  readonly note: string | null;
  readonly reconnect: () => void;
} {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const reconnect = (): void => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    void onReconnect(connectorId)
      .then((result) => {
        if (!result.ok) setNote(result.note);
      })
      .catch(() => setNote('The reconnect could not be started.'))
      .finally(() => {
        setBusy(false);
        setConfirming(false);
      });
  };
  return { confirming, setConfirming, busy, note, reconnect };
}

/** The confirmation strip the reconnect control expands into — it states the cost of the act, in plain English. */
function ReconnectConfirmation({
  connectorId,
  systemLabel,
  busy,
  onCancel,
  onConfirm,
}: {
  readonly connectorId: string;
  readonly systemLabel: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  return (
    <div className="space-y-2" role="group" aria-label={`Reconnect ${systemLabel}`}>
      <p className="text-xs text-text-subtle">
        {`Ritona stops reading ${systemLabel} until you finish signing in.`}
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="shrink-0 rounded-sm border border-border px-3 py-1.5 text-sm text-text-subtle hover:border-border-strong disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          data-reconnect={connectorId}
          disabled={busy}
          onClick={onConfirm}
          aria-label={`Confirm reconnecting the ${systemLabel} connector`}
          className={ACTION_BUTTON}
        >
          {busy ? 'Starting…' : 'Confirm reconnect'}
        </button>
      </div>
    </div>
  );
}

/** The Connector's state/health meta lines — NEVER a credential value, only a presence marker (G-140). */
function ConnectorMeta({
  connector,
}: {
  readonly connector: ConnectorStateView;
}): React.JSX.Element {
  return (
    <div className="mt-2 space-y-0.5">
      <MetaLine label="Mode">
        {connector.mode === 'read_write' ? 'read & write' : 'read only'}
      </MetaLine>
      <MetaLine label="Last refresh">{connector.lastRefresh ?? 'never'}</MetaLine>
      {connector.lastError !== null ? (
        <MetaLine label="Last error">{connectorErrorLabel(connector.lastError)}</MetaLine>
      ) : null}
      {connector.orgIdentity !== null ? (
        <MetaLine label="Org">{connector.orgIdentity.displayName}</MetaLine>
      ) : null}
      {/* A credential-PRESENCE marker ONLY — never the value (G-140). The presence is a projection-DERIVED
          boolean surfaced as a queryable data signal (data-credential-presence), never a token/secret value. */}
      <MetaLine label="Credential">
        <span data-credential-presence={connector.hasCredential ? 'held' : 'absent'}>
          {connector.hasCredential ? 'held (value never shown)' : 'not held'}
        </span>
      </MetaLine>
    </div>
  );
}

/** Keep the one recoverable OAuth reset state useful to a human; all other bounded classes stay unchanged. */
function connectorErrorLabel(lastError: string): string {
  return lastError === 'xero_permissions_reset_reconnect_required'
    ? 'Old Xero permissions cleared — connect once more'
    : lastError;
}

/** The Connector's active/revoked write scopes — the scope string + status only (no secret ref, G-140). */
function ConnectorScopes({
  scopes,
}: {
  readonly scopes: readonly ConnectorStateView['scopes'][number][];
}): React.JSX.Element | null {
  if (scopes.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
      {scopes.map((scope) => (
        <span
          key={scope.scope}
          data-scope={scope.scope}
          className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs ${
            scope.active
              ? 'border-sage bg-forest-tint text-forest'
              : 'border-border bg-surface-sunken text-text-subtle line-through'
          }`}
        >
          {scope.scope}
        </span>
      ))}
    </div>
  );
}
